import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import { GTFonts, theme } from '../../theme';
import { fs, s, v } from '../../utils/libs';

export type SlidingSegmentOption = {
  key: string;
  label: string;
};

export type SlidingSegmentedControlColors = {
  track: string;
  thumb: string;
  activeLabel: string;
  inactiveLabel: string;
  inactiveSegmentBg?: string;
};

export type SlidingSegmentedControlShape = 'pill' | 'box';

type SlidingSegmentedControlProps = {
  options: SlidingSegmentOption[];
  activeIndex: number;
  onChange: (index: number) => void;
  colors: SlidingSegmentedControlColors;
  disabled?: boolean;
  /**
   * `box` (default): rounded rectangle (theme md).
   * `pill`: fully rounded.
   */
  shape?: SlidingSegmentedControlShape;
};

const TRACK_PADDING = s(3);

/**
 * Segmented control with a sliding thumb (same motion as Quick Actions), as a
 * pill or a box.
 */
const SlidingSegmentedControl = ({
  options,
  activeIndex,
  onChange,
  colors,
  disabled,
  shape = 'box',
}: SlidingSegmentedControlProps) => {
  const box = shape === 'box';
  const [trackWidth, setTrackWidth] = useState(0);
  const slotWidth =
    options.length > 0
      ? Math.max((trackWidth - TRACK_PADDING * 2) / options.length, 0)
      : 0;
  const offset = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(offset, {
      toValue: activeIndex * slotWidth,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [activeIndex, offset, slotWidth]);

  const handleLayout = (event: LayoutChangeEvent) =>
    setTrackWidth(event.nativeEvent.layout.width);

  return (
    <View
      onLayout={handleLayout}
      style={[
        styles.track,
        box && styles.trackBox,
        { backgroundColor: colors.track },
      ]}>
      {slotWidth > 0 ? (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.thumb,
            box && styles.thumbBox,
            {
              width: slotWidth,
              backgroundColor: colors.thumb,
              transform: [{ translateX: offset }],
            },
          ]}
        />
      ) : null}
      {options.map((option, index) => (
        <TouchableOpacity
          key={option.key}
          disabled={disabled}
          onPress={() => onChange(index)}
          style={[
            styles.segment,
            index !== activeIndex &&
              colors.inactiveSegmentBg != null && {
                backgroundColor: colors.inactiveSegmentBg,
                borderRadius: box
                  ? theme.borderRadius.md
                  : theme.borderRadius.round,
              },
          ]}
          accessibilityRole="button"
          accessibilityLabel={option.label}
          accessibilityState={{ selected: index === activeIndex }}>
          <Text
            numberOfLines={1}
            // A label too long for its slot (narrow phone, large system
            // text) shrinks on native instead of ending in "…".
            adjustsFontSizeToFit
            style={[
              styles.segmentLabel,
              {
                color:
                  index === activeIndex
                    ? colors.activeLabel
                    : colors.inactiveLabel,
              },
            ]}>
            {option.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

export default SlidingSegmentedControl;

const styles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.borderRadius.round,
    padding: TRACK_PADDING,
  },
  thumb: {
    position: 'absolute',
    top: TRACK_PADDING,
    bottom: TRACK_PADDING,
    left: TRACK_PADDING,
    borderRadius: theme.borderRadius.round,
  },
  // Track corners follow the thumb's, one track padding further out
  trackBox: {
    borderRadius: theme.borderRadius.md + TRACK_PADDING,
  },
  thumbBox: {
    borderRadius: theme.borderRadius.md,
  },
  segment: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: v(7),
    // Labels are centred; the slim side padding leaves room for five
    // options ("This month") on a narrow phone.
    paddingHorizontal: s(2),
  },
  segmentLabel: {
    fontSize: fs(12),
    fontFamily: GTFonts.semibold,
    lineHeight: fs(14),
  },
});
